import React, { useState } from 'react';
import { Box } from '@mui/material';
import { useFileExplorerContext } from '../FileExplorerContext';
import { CompareModeBanner } from '../modules/PreviewSidebar/CompareModeBanner';

/**
 * FullLayout - Layout with file browser and preview sidebar
 *
 * Used for full-page file management (FilesPage, TaskAttachments)
 * Features:
 * - Main content area for file/folder listing
 * - Resizable preview sidebar
 * - Compare mode support (dual preview)
 */
export const FullLayout = ({ children, previewSidebar }) => {
  const { selectedFiles, compareMode, setCompareMode } = useFileExplorerContext();
  const [sidebarWidth, setSidebarWidth] = useState(320);
  const [isResizing, setIsResizing] = useState(false);

  const hasSelection = selectedFiles.length > 0;
  const showSidebar = hasSelection && previewSidebar;

  // Handle sidebar resizing
  const handleMouseDown = (e) => {
    setIsResizing(true);
    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (!isResizing) return;

    const newWidth = window.innerWidth - e.clientX;
    const minWidth = 100;
    const maxWidth = 2000;

    if (newWidth >= minWidth && newWidth <= maxWidth) {
      // Handle compare mode dubble width
      setSidebarWidth(
        (newWidth / (compareMode ? 2 : 1)) 
        - (compareMode ? 7 : 15)
      );
    }
  };

  const handleMouseUp = () => {
    setIsResizing(false);
  };

  React.useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  return (
    <>
      {/* Compare Mode Banner */}
      {compareMode && (
        <CompareModeBanner
          selectedFile={selectedFiles[0]}
          secondarySelectedFile={selectedFiles[1]}
          onExitCompareMode={() => setCompareMode(false)}
        />
      )}

      <Box sx={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
        {/* Main content area */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            minWidth: 0,
          }}
        >
          {children}
        </Box>

        {/* Resizable preview sidebar */}
        {showSidebar && (
          <>
            {/* Resize handle */}
            <Box
              onMouseDown={handleMouseDown}
              sx={{
                width: '4px',
                cursor: 'ew-resize',
                backgroundColor: 'divider',
                '&:hover': {
                  backgroundColor: 'primary.main',
                },
                ...(isResizing && {
                  backgroundColor: 'primary.main',
                }),
              }}
            />

            {/* Preview sidebar */}
            <Box
              sx={{
                width: compareMode ? sidebarWidth * 2 : sidebarWidth,
                boxSizing: "border-box",
                minWidth: compareMode ? 200 : 100,
                maxWidth: compareMode ? 4000 : 2000,
                overflow: 'auto',
                borderLeft: 1,
                borderColor: 'divider',
                backgroundColor: 'background.paper',
              }}
            >
              {previewSidebar}
            </Box>
          </>
        )}
      </Box>
    </>
  );
};
